import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {useCallback, useMemo} from "react";

import type {MediaSize} from "@khanacademy/wonder-blocks-layout";
import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {getPass} from "@khan/data-cookies";
import {RequestInfo} from "@khan/shared-core";

import type {Activity} from "../activities-list/types.ts";

export function useIsActivityPublic() {
    const USCivicsActivityFlag =
        useFeatureIsOn<AppFeatures>("us-civics-activity") ||
        getPass("us-civics-activity");

    const TutorMeStemCloneActivityFlag =
        useFeatureIsOn<AppFeatures>("tutor-me-stem-clone") ||
        getPass("tutor-me-stem-clone");

    return useCallback(
        (slug: string): boolean => {
            switch (slug) {
                case "activity-academic-essay-feedback":
                    return false;
                case "activity-us-civics":
                    return USCivicsActivityFlag === true;
                case "activity-help-me-with-a-math-problem":
                    return TutorMeStemCloneActivityFlag === true;
                default:
                    return true;
            }
        },
        [USCivicsActivityFlag, TutorMeStemCloneActivityFlag],
    );
}

/**
 * Hook to determine if a given activity should be available with a loading
 * state.
 *
 * Note: if you don't want an activity to be displayed in the MOBILE APP
 * activities list at all, update the code in the filterFromMobileList
 * resolver to return true.
 */
export function useIsActivityAvailable(): (
    activity: Activity,
    mediaSize?: MediaSize,
) => boolean {
    const isActivityPublic = useIsActivityPublic();

    const isActivityAvailable = useCallback(
        (activity: Activity, mediaSize?: MediaSize) => {
            if (
                activity.worksInMobileApp === false &&
                RequestInfo.isMobileApp
            ) {
                return false;
            }
            if (
                activity.worksOnSmallLayouts === false &&
                mediaSize === "small"
            ) {
                return false;
            }

            return isActivityPublic(activity.slug);
        },
        [isActivityPublic],
    );

    return useMemo(() => isActivityAvailable, [isActivityAvailable]);
}
